import { Connector } from '@web3-react/types'
import produce from 'immer'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { ConnectionType } from './connection/connection'
import { getConnection } from './connection/utils'

const STATE_STORAGE_KEY = 'WALLET_CONNECTION_CONFIG'

const DEFAULT_STATE = {
    connectedWallets: [],
    selectedWallet: undefined,
    errorByConnectionType: {
        [ConnectionType.INJECTED]: undefined,
        [ConnectionType.WALLET_CONNECT]: undefined,
        [ConnectionType.COINBASE_WALLET]: undefined,
        [ConnectionType.NETWORK]: undefined,
        [ConnectionType.GNOSIS_SAFE]: undefined,
    },
}

const initialState = () => {
    const state = localStorage.getItem(STATE_STORAGE_KEY)
    if (!state) return DEFAULT_STATE
    return JSON.parse(state)
}

type ConnectionError = { connectionType: ConnectionType; error: string | undefined }

export type Wallet = {
    walletType: string
    account: string
}

type IConnectedWalletContext =
    | {
          selectedWallet?: ConnectionType
          setSelectedWallet: (wallet?: ConnectionType) => void
          connectedWallets: Wallet[]
          removeConnectedWallet: (wallet: Wallet) => void
          addConnectedWallet: (wallet: Wallet) => void
          updateConnectionError: (error: ConnectionError) => void
          getErrorByConnector: (connector: Connector) => string | undefined
      }
    | undefined

const ConnectedWalletState = React.createContext<IConnectedWalletContext>(undefined)

export const useConnectedWallet = () => {
    const context = useContext(ConnectedWalletState)
    if (!context)
        throw new Error('Cannot use useConnectedWallet outside of a <ConnectedWalletProvider />')
    return context
}

export const ConnectedWalletProvider: React.FC = ({ children }) => {
    const [walletState, setWalletState] = useState<{
        connectedWallets: Wallet[]
        selectedWallet?: ConnectionType
        errorByConnectionType: Record<ConnectionType, string | undefined>
    }>(initialState())

    useEffect(() => {
        localStorage.setItem(STATE_STORAGE_KEY, JSON.stringify(walletState))
    }, [walletState])

    const { selectedWallet, connectedWallets, errorByConnectionType } = walletState

    const setSelectedWallet = (wallet?: ConnectionType) => {
        setWalletState((current) =>
            produce(current, (draft) => {
                draft.selectedWallet = wallet
                return draft
            })
        )
    }

    const addConnectedWallet = (wallet: Wallet) => {
        const existsAlready = connectedWallets.find(
            (_wallet) =>
                _wallet.walletType === wallet.walletType && _wallet.account === wallet.account
        )
        if (!existsAlready) {
            setWalletState((current) =>
                produce(current, (draft) => {
                    draft.connectedWallets = draft.connectedWallets.concat(wallet)
                    return draft
                })
            )
        }
    }

    const updateConnectionError = (data: ConnectionError) => {
        const { connectionType, error } = data
        setWalletState((current) =>
            produce(current, (draft) => {
                draft.errorByConnectionType[connectionType] = error
                return draft
            })
        )
    }

    const removeConnectedWallet = useCallback((wallet: Wallet) => {
        setWalletState((current) =>
            produce(current, (draft) => {
                draft.selectedWallet = undefined
                draft.connectedWallets = draft.connectedWallets.filter(
                    (_wallet) =>
                        !(
                            _wallet.walletType === wallet.walletType &&
                            _wallet.account === wallet.account
                        )
                )
                return draft
            })
        )
    }, [])

    const getErrorByConnector = (connector: Connector) => {
        return errorByConnectionType[getConnection(connector).type] ?? undefined
    }

    return (
        <ConnectedWalletState.Provider
            value={{
                selectedWallet,
                setSelectedWallet,
                connectedWallets,
                removeConnectedWallet,
                addConnectedWallet,
                updateConnectionError,
                getErrorByConnector,
            }}
        >
            {children}
        </ConnectedWalletState.Provider>
    )
}
